import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import {getFeatureFlagStatus} from 'client/components/FeatureFlag/featureFlag.utils';

const THEME_STORAGE_KEY = 'albert-theme-preference';
const DARK_MODE_CLASS = 'dark';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';

const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

interface ThemeContextProps {
  theme: Theme;
  themePreference: ThemePreference;
  isDarkMode: boolean;
  setThemePreference: (preference: ThemePreference) => void;
  isFeatureEnabled: boolean;
  isLoading: boolean;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'light',
  themePreference: 'system',
  isDarkMode: false,
  setThemePreference: () => {},
  isFeatureEnabled: false,
  isLoading: true
});

export const ThemeConsumer = ThemeContext.Consumer;

const subscribeToSystemTheme = (onChange: () => void) => {
  const mediaQueryList = window.matchMedia(SYSTEM_DARK_QUERY);
  mediaQueryList.addEventListener('change', onChange);
  return () => {
    mediaQueryList.removeEventListener('change', onChange);
  };
};

const getSystemTheme = (): Theme =>
  window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light';

interface ThemeProviderProps extends PropsWithChildrenRequired {
  /**
   * Bypasses the async `color_mode_dark` feature-flag check and uses this value directly.
   * Storybook has no backend to check the real flag against, so it passes this explicitly;
   * production never sets it, so the real flag continues gating dark mode there as normal.
   */
  featureEnabledOverride?: boolean;
}

const readStoredPreference = (): ThemePreference => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return stored && THEME_PREFERENCES.includes(stored as ThemePreference)
    ? (stored as ThemePreference)
    : 'system';
};

export const ThemeProvider = ({children, featureEnabledOverride}: ThemeProviderProps) => {
  // Lazy initializer: with the override, the stored preference is known synchronously at mount,
  // so there's no need for an effect to "adjust" it afterward.
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    featureEnabledOverride ? readStoredPreference() : 'system'
  );
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(featureEnabledOverride ?? false);
  const [isLoading, setIsLoading] = useState(featureEnabledOverride === undefined);
  const systemTheme = useSyncExternalStore(subscribeToSystemTheme, getSystemTheme);

  useEffect(() => {
    // The override skips this entirely — handled synchronously above instead.
    if (featureEnabledOverride !== undefined) return;

    const initializeTheme = async () => {
      try {
        const featureEnabled = await getFeatureFlagStatus('color_mode_dark');
        setIsFeatureEnabled(featureEnabled);

        if (featureEnabled) {
          setThemePreferenceState(readStoredPreference());
        }
      } catch (error) {
        console.error('Error initializing theme:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTheme();
  }, [featureEnabledOverride]);

  const theme: Theme = themePreference === 'system' ? systemTheme : themePreference;
  const isDarkMode = isFeatureEnabled && theme === 'dark';

  useEffect(() => {
    document.documentElement.classList.toggle(DARK_MODE_CLASS, isDarkMode);
  }, [isDarkMode]);

  const setThemePreference = useCallback(
    (preference: ThemePreference) => {
      if (!isFeatureEnabled) return;

      localStorage.setItem(THEME_STORAGE_KEY, preference);
      setThemePreferenceState(preference);
    },
    [isFeatureEnabled]
  );

  const value: ThemeContextProps = useMemo(
    () => ({
      theme,
      themePreference,
      isDarkMode,
      setThemePreference,
      isFeatureEnabled,
      isLoading
    }),
    [theme, themePreference, isDarkMode, setThemePreference, isFeatureEnabled, isLoading]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
